import type { CurrencyCode } from '@noths/polaris-client-localisation';
import type { SerializedError } from '@reduxjs/toolkit';

import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { SortOption } from 'src/redux/productsQuery/types/SortOption';
import type { Product } from './types/Product';

export interface RequestProductsMetadata {
  facetTitle: string;
  optionActive: boolean;
  optionTitle: string;
}
export enum TriggerName {
  CHANGE_FACETS = 'changeFacets',
  CHANGE_SORT = 'changeSort',
  LOAD_MORE_PRODUCTS = 'loadMoreProducts',
  LOAD_PREVIOUS_PRODUCTS = 'loadPreviousProducts',
  PAGE_NUMBER_CHANGE = 'pageNumberChange',
  RESET_ALL_FACETS_AND_SORT = 'resetFacetsAndSort',
  RESET_FACET = 'resetFacet',
}

interface ProviderMetadata {
  filter: string;
}

// return format of adapter
export interface RequestProductsActionProductsPayload {
  currencyCode: CurrencyCode;
  errorCode?: string;
  errorMessage?: string;
  lastPageLoaded: number;
  /**
   * Where the next batch's organic results start, when this batch carried
   * sponsored products: the listings API's `metadata.sponsored.nextOffset`.
   * Absent for an organic-only response, whose next offset is the page arithmetic.
   */
  organicNextOffset?: number;
  perPage: number;
  products: Product[];
  productsReturnedCount: number;
  providerAttributionToken: string | null;
  providerMetadata: ProviderMetadata | null;
  term: string | null;
  totalPages: number;
  totalProducts: number;
}

// return format of requestProducts thunk; used in reducer
export interface RequestProductsActionPayload {
  facets?: Facet[];
  isFacetPillSurface?: boolean;
  isTaxonomyNavigation?: boolean;
  metadata?: RequestProductsMetadata;
  products?: RequestProductsActionProductsPayload;
  sortOptions?: SortOption[];
  triggerName?: TriggerName;
}

export interface ProductsState {
  currencyCode: CurrencyCode;
  deliveryZone?: string;
  error?: SerializedError | null;
  lastPageLoaded: number | null;
  /**
   * The organic cursor each loaded batch ended at, by page number, for batches that
   * carried sponsored products. The next batch starts its organic results there
   * rather than at `PAGE_SIZE * (page - 1)`, since sponsored products displaced
   * some organic ones and duplicates were skipped.
   */
  organicCursors: Record<number, number>;
  pageLoading: number | null;
  pages: Record<number, Product[]>;
  productsReturnedCount: number;
  providerAttributionToken: string | null;
  providerMetadata: ProviderMetadata | null;
  showFewResultsMessage?: boolean;
  totalPages: number;
  totalProducts: number;
}
